/**
 * Combat Feel Phase C — high-signal audiovisual identity for key affixes.
 * Soup stays capped: only these IDs get tint/wash/stinger treatment.
 */

export type AffixVisualMode = "projectile_tint" | "ship_wash" | "shield_aura" | "trail_boost";

export type AffixIdentity = {
  id: string;
  /** Short HUD chip label. */
  chip: string;
  /** Hex color for chip / wash / flash. */
  color: number;
  /** CSS color for HUD chips. */
  css: string;
  /** Higher wins sole projectile tint override. */
  tintPriority: number;
  modes: readonly AffixVisualMode[];
  /** Projectile color when this affix wins tint priority. */
  projectileTint?: number;
  /** Ship emissive wash while owned. */
  washEmissive?: number;
};

/** Priority order for sole projectile tint (first match among owned wins). */
export const PROJECTILE_TINT_PRIORITY: readonly string[] = [
  "hot_barrels",
  "glass_cannon",
  "coolant_loop",
];

export const AFFIX_IDENTITIES: Record<string, AffixIdentity> = {
  hot_barrels: {
    id: "hot_barrels",
    chip: "HOT",
    color: 0xe87830,
    css: "#e87830",
    tintPriority: 30,
    modes: ["projectile_tint", "ship_wash"],
    projectileTint: 0xff9a40,
    washEmissive: 0xc45c18,
  },
  coolant_loop: {
    id: "coolant_loop",
    chip: "COOL",
    color: 0x5ab4e0,
    css: "#5ab4e0",
    tintPriority: 20,
    modes: ["projectile_tint", "ship_wash"],
    projectileTint: 0x7ec8f0,
    washEmissive: 0x2a6a88,
  },
  glass_cannon: {
    id: "glass_cannon",
    chip: "GLASS",
    color: 0xe05050,
    css: "#e05050",
    tintPriority: 25,
    modes: ["projectile_tint", "ship_wash"],
    projectileTint: 0xff7070,
    washEmissive: 0x8a2020,
  },
  nap_predator: {
    id: "nap_predator",
    chip: "PRED",
    color: 0xc9a227,
    css: "#c9a227",
    tintPriority: 0,
    modes: ["ship_wash"],
    washEmissive: 0x6a4518,
  },
  hard_shell: {
    id: "hard_shell",
    chip: "SHELL",
    color: 0x6ec6ff,
    css: "#6ec6ff",
    tintPriority: 0,
    modes: ["shield_aura"],
  },
  shield_weave: {
    id: "shield_weave",
    chip: "WEAVE",
    color: 0x8ad4ff,
    css: "#8ad4ff",
    tintPriority: 0,
    modes: ["shield_aura"],
  },
  afterburn_trim: {
    id: "afterburn_trim",
    chip: "BURN",
    color: 0xff8a4a,
    css: "#ff8a4a",
    tintPriority: 0,
    modes: ["trail_boost", "ship_wash"],
    washEmissive: 0xa04a20,
  },
};

export const SIGNAL_AFFIX_IDS = Object.keys(AFFIX_IDENTITIES);

export function affixIdentity(id: string): AffixIdentity | null {
  return AFFIX_IDENTITIES[id] ?? null;
}

/** Highest-priority projectile tint among owned signal affixes, or null. */
export function resolveProjectileTint(owned: readonly string[]): number | null {
  let best: AffixIdentity | null = null;
  for (const id of owned) {
    const identity = AFFIX_IDENTITIES[id];
    if (!identity?.projectileTint) continue;
    if (!best || identity.tintPriority > best.tintPriority) best = identity;
  }
  return best?.projectileTint ?? null;
}

/** Dominant ship wash emissive (non-shield) among owned signal affixes. */
export function resolveShipWash(owned: readonly string[]): number | null {
  const order = ["glass_cannon", "hot_barrels", "coolant_loop", "afterburn_trim"];
  for (const id of order) {
    if (!owned.includes(id)) continue;
    const wash = AFFIX_IDENTITIES[id]?.washEmissive;
    if (wash !== undefined) return wash;
  }
  return null;
}

export function hasShieldAura(owned: readonly string[]): boolean {
  return owned.includes("hard_shell") || owned.includes("shield_weave");
}

export function hasTrailBoost(owned: readonly string[]): boolean {
  return owned.includes("afterburn_trim");
}

/** Chip rows for HUD — signal affixes only, in inventory order. */
export function activeAffixChips(
  owned: readonly string[],
): { id: string; chip: string; css: string; active: boolean }[] {
  return owned
    .map((id) => {
      const identity = AFFIX_IDENTITIES[id];
      if (!identity) return null;
      return {
        id,
        chip: identity.chip,
        css: identity.css,
        active: true,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);
}

export function pickupFlashColor(affixId: string | undefined): number {
  if (!affixId) return 0xd0d4d8;
  return AFFIX_IDENTITIES[affixId]?.color ?? 0xd0d4d8;
}
